import type { DropdownOption } from '../types';

/** Default fallback color for status options without a color */
export const DEFAULT_STATUS_COLOR = '#9e9e9e';

/** Default fallback color for boards without a color */
export const DEFAULT_BOARD_COLOR = '#1976d2';

/** Column keys rendered as dedicated fields — excluded from the dynamic columns section */
export const EXCLUDED_COLUMN_KEYS = new Set([
  'description',
  'notes',
  'status',
  'priority',
  'assigned_to',
]);

/** Default status options (fallback when board columns are not loaded) */
export const DEFAULT_STATUS_OPTIONS: DropdownOption[] = [
  { id: 'todo', label: 'Todo', color: '#9e9e9e', category: 'not_started' },
  { id: 'in_progress', label: 'In Progress', color: '#2196f3', category: 'active' },
  { id: 'review', label: 'Review', color: '#ff9800', category: 'active' },
  { id: 'done', label: 'Done', color: '#4caf50', category: 'done' },
  { id: 'cancelled', label: 'Cancelled', color: '#f44336', category: 'cancelled' },
];

export const PRIORITY_VALUES = ['low', 'medium', 'high', 'critical'] as const;

/** Default colors for priority badges (fallback when board has no custom priority options) */
export const PRIORITY_COLORS: Record<string, string> = {
  low: '#8bc34a',
  medium: '#2196f3',
  high: '#ff9800',
  critical: '#f44336',
};

/** Default priority options with colors (fallback when board columns are not loaded) */
export const DEFAULT_PRIORITY_OPTIONS: DropdownOption[] = [
  { id: 'low', label: 'Low', color: '#8bc34a' },
  { id: 'medium', label: 'Medium', color: '#2196f3' },
  { id: 'high', label: 'High', color: '#ff9800' },
  { id: 'critical', label: 'Critical', color: '#f44336' },
];

/**
 * Resolve priority color from board column options, falling back to PRIORITY_COLORS constant.
 * Use this instead of accessing PRIORITY_COLORS directly to respect board-level customization.
 */
export function getPriorityColor(priority: string, priorityOptions?: DropdownOption[]): string {
  if (priorityOptions) {
    const opt = priorityOptions.find((o) => o.id === priority);
    if (opt?.color) return opt.color;
  }
  return PRIORITY_COLORS[priority] || '#9e9e9e';
}

/** Extract priority options from board columns, falling back to DEFAULT_PRIORITY_OPTIONS */
export function resolvePriorityOptions(columns?: { column_key: string; settings: { options?: DropdownOption[] } }[]): DropdownOption[] {
  if (columns) {
    const priorityColumn = columns.find((col) => col.column_key === 'priority');
    if (priorityColumn?.settings?.options && priorityColumn.settings.options.length > 0) {
      return priorityColumn.settings.options;
    }
  }
  return DEFAULT_PRIORITY_OPTIONS;
}

/** Colors for status category groupings (not_started, active, done, cancelled) */
export const CATEGORY_COLORS: Record<string, string> = {
  not_started: '#9e9e9e',
  active: '#2196f3',
  done: '#4caf50',
  cancelled: '#f44336',
};

/** Tailwind class overrides to make MultiUserPicker render inline (borderless, compact) */
export const MULTI_USER_PICKER_INLINE_CLASS =
  '[&_button]:!border-0 [&_button]:!shadow-none [&_button]:!bg-transparent [&_button]:hover:!bg-muted/50 [&_button]:!h-8 [&_button]:!min-h-0 [&_button]:!px-3 [&_button]:!py-0';

/** Returns translated fallback priority options for dropdowns. */
export function getDefaultPriorityOptions(t: (key: string) => string): DropdownOption[] {
  return PRIORITY_VALUES.map((id) => ({
    id,
    label: t(`priorities.${id}`),
  }));
}
